<template>
    <div class="hint-page">
        <kgr-loading v-if="loading" />
        <template v-else>
            <div
                v-if="openIsEmpty"
                class="applet"
            >
                {{ $t('no_hints') }}
            </div>

            <div
                v-for="section in renderedSections"
                v-show="section.visible"
                :key="section.key"
                class="hint-section"
            >
                <h2
                    v-if="section.title"
                    class="kgr-headline kgr-headline--h3 kgr-headline--text"
                >
                    {{ section.title }}
                </h2>

                <kgr-accordion>
                    <kgr-accordion-item
                        v-for="category in section.categories"
                        :key="section.key + '-' + category.key"
                        :title="category.label + ' (' + category.count + ')'"
                        @opened="openCategory(section.key, category)"
                        @closed="category.expanded = false"
                    >
                        <div
                            v-if="category.expanded"
                            class="hint-category__body"
                        >
                            <table class="kgr-table kgr-table--striped kgr-table--block">
                                <thead>
                                    <tr class="kgr-table__row">
                                        <th class="kgr-table__cell kgr-table__cell--headline">{{ $t('Hinweise') }}</th>
                                        <th class="kgr-table__cell kgr-table__cell--headline"></th>
                                        <th class="kgr-table__cell kgr-table__cell--headline">{{ $t('Lösung') }}</th>
                                        <th class="kgr-table__cell kgr-table__cell--headline"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="problem in category.problems"
                                        :key="problem.id"
                                        class="kgr-table__row kgr-table__row--top"
                                    >
                                        <td class="kgr-table__cell">
                                            <!-- eslint-disable-next-line vue/no-v-html -->
                                            <div v-html="problem.hint"></div>
                                        </td>
                                        <td class="kgr-table__cell hint-page__action-cell">
                                            <kgr-loading
                                                v-show="problem.rowBusy"
                                                inline
                                            />
                                            <SphButton
                                                v-show="!problem.rowBusy && !problem.isResolved"
                                                variant="ghost"
                                                :icon="RefreshCw"
                                                :title="$t('Aktualisieren')"
                                                :aria-label="$t('Aktualisieren')"
                                                @click="update(section.key, category, problem)"
                                            />
                                            <i
                                                v-if="problem.isResolved"
                                                :class="resolutionIsInfo(problem) ? 'icon-info' : 'icon-check'"
                                                :style="{ color: resolutionIsInfo(problem) ? '#2196F3' : '#4CAF50' }"
                                            ></i>
                                        </td>
                                        <td class="kgr-table__cell">
                                            <span v-if="problem.isResolved">{{ resolutionLabel(problem) }}</span>
                                            <kgr-select
                                                v-else
                                                v-model="problem.solution"
                                                :options="problem.solutions"
                                                :disabled="problem.rowBusy"
                                                no-null-option
                                                display="label"
                                            />
                                        </td>
                                        <td class="kgr-table__cell">
                                            <kgr-button
                                                v-if="!problem.isResolved"
                                                type="red"
                                                :disabled="problem.rowBusy || !problem.solution"
                                                @click="solve(section.key, category, problem)"
                                            >
                                                {{ $t('ausführen') }}
                                            </kgr-button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <kgr-loading v-show="category.busy" />
                            <kgr-button
                                v-show="!category.busy && hasMore(category)"
                                :value="$t('Mehr anzeigen')"
                                type="grey"
                                @click="loadMore(section.key, category)"
                            />
                        </div>
                    </kgr-accordion-item>
                </kgr-accordion>
            </div>

            <button
                v-if="ignoredForUserTotal > 0 && !showIgnoredForUser"
                type="button"
                class="kgr-button"
                @click="showIgnoredForUser = true"
            >
                {{ $t('Ausgeblendete und ignorierte Hinweise anzeigen') }} ({{ ignoredForUserTotal }})
            </button>

            <button
                v-if="ignoredTotal > 0 && !showIgnored"
                type="button"
                class="kgr-button"
                @click="showIgnored = true"
            >
                {{ $t('Hinweise ohne Handlungsbedarf anzeigen') }} ({{ ignoredTotal }})
            </button>
        </template>
    </div>
</template>
<script>
import Accordion from '../../components/common/accordion/Accordion.vue';
import AccordionItem from '../../components/common/accordion/AccordionItem.vue';
import GenericSelect from '../../components/common/form/select/GenericSelect.vue';
import KgrButton from '../../components/common/form/button/KgrButton.vue';
import Loading from '../../components/common/Loading.vue';
import { RefreshCw } from 'lucide';
import { SphButton } from '@/components/base/button/index.ts';
import hintRepositoryFor from '../../js/repository/hintRepositoryFor';

const SECTION_OPEN = 'open';
const SECTION_IGNORED_FOR_USER = 'ignored_for_user';
const SECTION_IGNORED = 'ignored';

export default {
    name: 'HintPage',
    components: {
        'kgr-accordion': Accordion,
        'kgr-accordion-item': AccordionItem,
        'kgr-button': KgrButton,
        'kgr-loading': Loading,
        'kgr-select': GenericSelect,
        'SphButton': SphButton,
    },
    props: {
        institutionType: {
            type: String,
            default: 'club',
            validator: (value) => ['club', 'kindergarten'].includes(value),
        },
        institutionId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            RefreshCw,
            loading: true,
            showIgnoredForUser: false,
            showIgnored: false,
            sections: {
                [SECTION_OPEN]: [],
                [SECTION_IGNORED_FOR_USER]: [],
                [SECTION_IGNORED]: [],
            },
        };
    },
    computed: {
        repository() {
            return hintRepositoryFor(this.institutionType);
        },
        openIsEmpty() {
            return this.sections[SECTION_OPEN].length === 0;
        },
        ignoredForUserTotal() {
            return this.totalFor(SECTION_IGNORED_FOR_USER);
        },
        ignoredTotal() {
            return this.totalFor(SECTION_IGNORED);
        },
        renderedSections() {
            // All sections render from mount; the ignored / ignored-for-user ones are hidden via v-show until
            // revealed (their counts are still needed for the reveal buttons).
            return [
                { key: SECTION_OPEN, title: null, visible: true, categories: this.sections[SECTION_OPEN] },
                { key: SECTION_IGNORED_FOR_USER, title: this.$t('Ausgeblendete und ignorierte Hinweise'), visible: this.showIgnoredForUser, categories: this.sections[SECTION_IGNORED_FOR_USER] },
                { key: SECTION_IGNORED, title: this.$t('Hinweise ohne Handlungsbedarf'), visible: this.showIgnored, categories: this.sections[SECTION_IGNORED] },
            ];
        },
    },
    created() {
        this.loadAllCategories();
    },
    methods: {
        totalFor(section) {
            return this.sections[section].reduce((sum, category) => sum + category.count, 0);
        },
        loadAllCategories() {
            this.loading = true;
            Promise.all([
                this.loadCategories(SECTION_OPEN),
                this.loadCategories(SECTION_IGNORED_FOR_USER),
                this.loadCategories(SECTION_IGNORED),
            ]).finally(() => {
                this.loading = false;
            });
        },
        loadCategories(section) {
            return this.repository.getCategories(this.institutionId, section)
                .then((categories) => {
                    this.sections[section] = categories.map((category) => ({
                        key: category.key,
                        label: category.label,
                        count: category.count,
                        expanded: false,
                        problems: [],
                        page: 0,
                        totalPages: null,
                        busy: false,
                    }));
                })
                .catch(() => {
                    this.sections[section] = [];
                });
        },
        openCategory(section, category) {
            category.expanded = true;
            if (category.problems.length === 0) {
                this.loadMore(section, category);
            }
        },
        loadMore(section, category) {
            category.busy = true;
            this.repository.getProblems(this.institutionId, section, category.key, category.page + 1)
                .then(({ problems, pagination }) => {
                    problems.forEach((problem) => {
                        problem.solution = problem.solutions && problem.solutions.length > 0 ? problem.solutions[0] : null;
                        problem.rowBusy = false;
                        problem.isResolved = false;
                        problem.resolutionState = null;
                    });
                    category.problems = category.problems.concat(problems);
                    category.page = pagination.current_page;
                    category.totalPages = pagination.total_pages;
                })
                .finally(() => {
                    category.busy = false;
                });
        },
        hasMore(category) {
            return category.totalPages !== null && category.page < category.totalPages;
        },
        solve(section, category, problem) {
            if (problem.rowBusy || !problem.solution) {
                return;
            }
            problem.rowBusy = true;
            this.repository.solveProblem(problem.id, problem.solution.value)
                .then((result) => {
                    problem.resolutionState = this.resolutionStateFromResult(result);
                    problem.isResolved = true;
                    category.count = Math.max(0, category.count - 1);
                })
                .finally(() => {
                    problem.rowBusy = false;
                });
        },
        resolutionStateFromResult(result) {
            // Mirrors the legacy solve/refresh JS: outdated and follow-up/ignored take precedence over a plain
            // "resolved" so the row shows the same distinct outcome text + status icon it did before the migration.
            if (!result.isPresent) {
                return 'outdated';
            }
            if (result.hasFollowUp) {
                return 'follow_up';
            }
            if (result.isIgnored) {
                return 'ignored';
            }
            return 'done';
        },
        resolutionLabel(problem) {
            switch (problem.resolutionState) {
                case 'follow_up':
                    return this.$t('Wiedervorlage');
                case 'ignored':
                    return this.$t('Ignoriert');
                case 'outdated':
                    return this.$t('Nicht mehr relevant');
                default:
                    return this.$t('Erledigt');
            }
        },
        resolutionIsInfo(problem) {
            return problem.resolutionState === 'follow_up' || problem.resolutionState === 'ignored';
        },
        update(section, category, problem) {
            if (problem.rowBusy) {
                return;
            }
            problem.rowBusy = true;
            this.repository.updateProblem(problem.id)
                .then((result) => {
                    // Categories load on demand, so a re-evaluated problem no longer needs a full category
                    // reload to stay consistent: patch just this row. If it still belongs in this section, refresh
                    // its hint in place; otherwise it was resolved or moved to another section, so drop it.
                    if (this.stillBelongsToSection(section, result)) {
                        problem.hint = result.hint;
                        problem.rowBusy = false;
                    } else {
                        this.removeProblem(category, problem);
                    }
                })
                .catch(() => {
                    problem.rowBusy = false;
                });
        },
        stillBelongsToSection(section, result) {
            // Mirrors the backend section predicate (ProblemRepository::applySectionPredicate).
            if (!result.isPresent) {
                return false;
            }
            if (section === SECTION_IGNORED) {
                return result.isIgnored;
            }
            if (result.isIgnored) {
                return false;
            }
            if (section === SECTION_IGNORED_FOR_USER) {
                return result.hasFollowUp;
            }
            return !result.hasFollowUp;
        },
        removeProblem(category, problem) {
            const index = category.problems.indexOf(problem);
            if (index === -1) {
                // Nothing to remove (already gone / different reference): don't desync the count, and clear the
                // busy flag so a still-shown row doesn't keep spinning.
                problem.rowBusy = false;
                return;
            }
            category.problems.splice(index, 1);
            category.count = Math.max(0, category.count - 1);
        },
    },
};
</script>

<style scoped lang="scss">
    .hint-section {
        margin-bottom: 20px;
    }
    .hint-category__body {
        padding: 10px 0;
    }
    .hint-page__action-cell {
        // Stable width so swapping the refresh button for the busy spinner never reflows the column.
        width: 3rem;
        text-align: center;

        // The shared inline loader forces min-width: 100px; drop it here so the spinner fits the button's slot.
        :deep(.loading-wrapper.inline) {
            min-width: 0;
        }
    }
</style>
